import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { 
  Package, 
  TrendingUp, 
  Gift, 
  Monitor,
  User,
  BarChart3,
  CalendarDays,
  Settings as SettingsIcon,
  FileText,
  ListPlus,
  LineChart,
  Megaphone,
  ArrowLeft,
  Users,
  Bell,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import InteractiveTour from "@/components/InteractiveTour";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const modules = [
  {
    title: "Bestand",
    icon: BarChart3,
    path: "/uebersicht",
    description: "Bestand & Top 3 Artikel",
    color: "from-teal-500 to-green-600",
    dataTour: "bestand-button"
  },
  {
    title: "Eingangsliste",
    icon: TrendingUp,
    path: "/eingangsliste",
    description: "Wareneingang erfassen",
    color: "from-blue-500 to-cyan-600",
    dataTour: "eingangsliste-button"
  },
  {
    title: "Geschenke",
    icon: Gift,
    path: "/geschenke",
    description: "Geschenkartikel verwalten",
    color: "from-rose-500 to-pink-600",
    dataTour: "geschenke-button"
  },
  {
    title: "Werbeartikel",
    icon: Megaphone,
    path: "/werbeartikel",
    description: "Werbeartikel & Marketing",
    color: "from-cyan-500 to-blue-600",
    dataTour: "werbeartikel-button"
  },
  {
    title: "Vorführartikel",
    icon: Monitor,
    path: "/vorfuehrartikel",
    description: "Demo-Artikel erfassen",
    color: "from-indigo-500 to-violet-600",
    dataTour: "vorfuehrartikel-button"
  },
  {
    title: "Eigenverbrauch",
    icon: User,
    path: "/eigenverbrauch",
    description: "Eigenverbrauch dokumentieren",
    color: "from-slate-500 to-gray-600",
    dataTour: "eigenverbrauch-button"
  },
  {
    title: "Quartalsübersicht",
    icon: CalendarDays,
    path: "/quartalsuebersicht",
    description: "Quartalsdaten anzeigen",
    color: "from-cyan-500 to-blue-600",
    dataTour: "quartalsuebersicht-button"
  },
  {
    title: "Analyse",
    icon: LineChart,
    path: "/analyse",
    description: "Umsatz & Verkaufsanalysen",
    color: "from-purple-500 to-violet-600",
    dataTour: "analyse-button"
  },
  {
    title: "Preisliste",
    icon: ListPlus,
    path: "/preisliste",
    description: "Produkte und Preise verwalten",
    color: "from-emerald-500 to-teal-600",
    dataTour: "produktliste-button"
  }
];

export default function Dashboard() {
  const cardsRef = useRef([]);
  const [showNachrichten, setShowNachrichten] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showTourManual, setShowTourManual] = useState(false);

  useEffect(() => {
    // Check if coming from a subpage (has saved scroll position)
    const savedScrollPosition = sessionStorage.getItem('dashboardScrollPosition');
    const cameFromMainHub = sessionStorage.getItem('cameFromMainHub');
    
    if (cameFromMainHub === 'true') {
      // Coming from MainHub - scroll to top
      window.scrollTo(0, 0);
      sessionStorage.removeItem('cameFromMainHub');
    } else if (savedScrollPosition) {
      // Coming from subpage - restore position
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
      }, 50);
    } else {
      // First visit or unknown - scroll to top
      window.scrollTo(0, 0);
    }
    
    // Clear saved position after using it
    sessionStorage.removeItem('dashboardScrollPosition');

    // Check if animation has already run in this session
    const hasAnimated = sessionStorage.getItem('dashboardAnimated');
    
    if (hasAnimated) {
      // Just show all cards immediately with class, not inline styles
      cardsRef.current.forEach((card) => {
        if (card) {
          card.classList.add('show-instantly');
        }
      });
      // Load notifications after showing cards
      loadNotifications();
      return;
    }

    // Animate all cards with staggered delay - using individual timers
    const timers = [];
    cardsRef.current.forEach((card, index) => {
      if (card) {
        const timer = setTimeout(() => {
          card.classList.add('animate-fade-in-up');
        }, 100 + (index * 50));
        timers.push(timer);
      }
    });
    
    // Mark as animated after all animations complete
    const finalTimer = setTimeout(() => {
      sessionStorage.setItem('dashboardAnimated', 'true');
    }, 100 + (cardsRef.current.length * 50) + 400);
    timers.push(finalTimer);

    // Load notifications
    loadNotifications();

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/api/nachrichten`);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error("Fehler beim Laden der Benachrichtigungen:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex items-center justify-between gap-3 md:gap-4 flex-1 min-w-0">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-3xl font-bold text-slate-800 truncate">Inventory</h1>
                <p className="text-slate-500 mt-1 text-xs md:text-base">Bestand & Rechnungen verwalten</p>
              </div>
              {/* Logo rechts neben der Überschrift - immer sichtbar */}
              <Link to="/main-hub" title="Zu Mein Business">
                <img 
                  src="/burningteam-logo.png" 
                  alt="BurningTeam" 
                  className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover shadow-md hover:shadow-lg transition-shadow cursor-pointer flex-shrink-0"
                />
              </Link>
              
              {/* Compact Icon Navigation - Hidden on mobile */}
              <div className="hidden md:flex gap-2 ml-6">
                <Link to="/main-hub" title="Zurück zu Mein Business">
                  <Button 
                    variant="outline"
                    size="icon"
                    className="border-slate-300 hover:bg-slate-50 h-10 w-10"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/kunden" title="Zu Kunden">
                  <Button 
                    variant="outline"
                    size="icon"
                    className="border-emerald-300 hover:bg-emerald-50 text-emerald-700 h-10 w-10"
                  >
                    <Users className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/zielplanung" title="Zum Zielplanungskalender">
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-emerald-300 hover:bg-emerald-50 text-emerald-700 h-10 w-10"
                  >
                    <CalendarDays className="w-5 h-5" />
                  </Button>
                </Link>
                <Button 
                  onClick={() => setShowTourManual(true)}
                  variant="outline"
                  size="icon"
                  className="border-emerald-300 hover:bg-emerald-50 h-10 w-10"
                  title="Inventory-Bereich Tour starten"
                  data-tour="info-button-inventory"
                >
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Button>
              </div>
            </div>
            
            {/* Full Buttons */}
            <div className="flex gap-1.5 md:gap-3 flex-wrap">
              {/* Mobile Icon Navigation */}
              <div className="flex md:hidden gap-1.5">
                <Link to="/main-hub" title="Zurück zu Mein Business">
                  <Button 
                    variant="outline"
                    size="icon"
                    className="border-slate-300 hover:bg-slate-50 h-8 w-8"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kunden" title="Zu Kunden">
                  <Button 
                    variant="outline"
                    size="icon"
                    className="border-emerald-300 hover:bg-emerald-50 text-emerald-700 h-8 w-8"
                  >
                    <Users className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/zielplanung" title="Zum Zielplanungskalender">
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-emerald-300 hover:bg-emerald-50 text-emerald-700 h-8 w-8"
                  >
                    <CalendarDays className="w-4 h-4" />
                  </Button>
                </Link>
                <Button 
                  onClick={() => setShowTourManual(true)}
                  variant="outline"
                  size="icon"
                  className="border-emerald-300 hover:bg-emerald-50 h-8 w-8"
                  title="Inventory-Bereich Tour starten"
                  data-tour="info-button-inventory"
                >
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Button>
              </div>
              
              <Button 
                onClick={() => setShowNachrichten(true)}
                variant="outline" 
                className="border-slate-300 hover:bg-slate-50 text-xs md:text-base h-8 md:h-10 px-2.5 md:px-4 relative"
              >
                <Bell className="w-3.5 md:w-4 h-3.5 md:h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <Link to="/settings">
                <Button 
                  data-testid="settings-button"
                  variant="outline" 
                  className="border-slate-300 hover:bg-slate-50 text-xs md:text-base h-8 md:h-10 px-2.5 md:px-4"
                >
                  <SettingsIcon className="w-3.5 md:w-4 h-3.5 md:h-4 mr-1.5 md:mr-2" />
                  Profil
                </Button>
              </Link>
              <Link to="/rechnungen" data-tour="rechnungen-button">
                <Button 
                  data-testid="rechnungen-button"
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 text-xs md:text-base h-8 md:h-10 px-2.5 md:px-4"
                >
                  <FileText className="w-3.5 md:w-5 h-3.5 md:h-5 mr-1.5 md:mr-2" />
                  Rechnungen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((module, index) => (
            <Link 
              key={index} 
              to={module.path}
              data-testid={`module-${module.title.toLowerCase().replace(/\s+/g, '-')}`}
              data-tour={module.dataTour}
              ref={(el) => (cardsRef.current[index] = el)}
              onClick={() => {
                // Save current scroll position before navigating to subpage
                sessionStorage.setItem('dashboardScrollPosition', window.pageYOffset.toString());
                
                // Wenn Tour aktiv und Bestand geklickt wird, setze localStorage für Tour-Fortsetzung
                if (module.dataTour === 'bestand-button') {
                  const existingTour = localStorage.getItem('burningTeamTour');
                  // Prüfe ob Tour gerade bei inventory-bestand ist (Schritt 10)
                  if (existingTour || window.burningTeamTourActive) {
                    console.log('📊 Bestand geklickt während Tour - setze localStorage');
                    localStorage.setItem('burningTeamTour', JSON.stringify({
                      shouldStart: true,
                      page: 'uebersicht',
                      globalStep: 10
                    }));
                  }
                }
              }}
              style={{ 
                animationDelay: `${index * 50}ms`,
                '--index': index
              }}
            >
              <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-200 hover:border-slate-300 h-full transform hover:scale-105 hover:-translate-y-2 active:scale-[0.98] active:translate-y-0 cursor-pointer">
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${module.color}`}></div>
                
                <div className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg group-hover:shadow-xl`}>
                    <module.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-slate-800 mb-2 group-hover:text-emerald-600 transition-colors">
                    {module.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {module.description}
                  </p>
                </div>

                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-100 opacity-0 group-hover:opacity-50 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      
      {/* Nachrichten-Center Modal */}
      {showNachrichten && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">📬 Nachrichten-Center</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {unreadCount} {unreadCount === 1 ? 'Benachrichtigung' : 'Benachrichtigungen'}
                </p>
              </div>
              <button
                onClick={() => setShowNachrichten(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 text-lg">Keine Benachrichtigungen</p>
                  <p className="text-slate-400 text-sm mt-2">Alle Tasks sind erledigt! 🎉</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => {
                    const getPriorityStyles = (priority) => {
                      switch(priority) {
                        case "critical":
                          return "bg-red-50 border-red-600 hover:bg-red-100";
                        case "high_red":
                          return "bg-red-50 border-red-600 hover:bg-red-100";
                        case "high":
                          return "bg-orange-50 border-orange-500 hover:bg-orange-100";
                        case "high_blue":
                          return "bg-blue-50 border-blue-500 hover:bg-blue-100";
                        case "warning":
                          return "bg-yellow-50 border-yellow-500 hover:bg-yellow-100";
                        case "medium_orange":
                          return "bg-orange-50 border-orange-500 hover:bg-orange-100";
                        case "medium":
                          return "bg-blue-50 border-blue-500 hover:bg-blue-100";
                        case "success":
                          return "bg-green-50 border-green-500 hover:bg-green-100";
                        default:
                          return "bg-slate-50 border-slate-400 hover:bg-slate-100";
                      }
                    };

                    return (
                      <div
                        key={notif.id}
                        className={`p-4 rounded-lg border-l-4 transition-all ${getPriorityStyles(notif.priority)} cursor-pointer`}
                        onClick={() => {
                          // Navigiere zur Kunden-Seite mit diesem Teampartner
                          window.location.href = `/kunden?highlight=${notif.teampartner_id}`;
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-800">{notif.title}</p>
                            <p className="text-sm text-slate-600 mt-1">{notif.message}</p>
                            <p className="text-xs text-slate-500 mt-2 font-medium">{notif.person}</p>
                            {notif.termin_thema && (
                              <p className="text-xs text-slate-400 mt-1">Thema: {notif.termin_thema}</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  // API-Call zum Backend
                                  await axios.post(`${API}/api/nachrichten/dismiss/${notif.id}`);
                                  // Entferne lokal
                                  setNotifications(notifications.filter(n => n.id !== notif.id));
                                  setUnreadCount(prev => Math.max(0, prev - 1));
                                } catch (error) {
                                  console.error("Fehler beim Verwerfen:", error);
                                }
                              }}
                              className="text-slate-400 hover:text-slate-600 text-xs underline"
                              title="Verwerfen"
                            >
                              Verwerfen
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {notifications.length > 0 && (
              <div className="border-t border-slate-200 p-4 bg-slate-50">
                <button
                  onClick={async () => {
                    try {
                      // API-Call zum Backend
                      await axios.post(`${API}/api/nachrichten/dismiss-all`);
                      // Entferne lokal
                      setNotifications([]);
                      setUnreadCount(0);
                    } catch (error) {
                      console.error("Fehler beim Markieren:", error);
                    }
                  }}
                  className="w-full py-2 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                >
                  Alle als gelesen markieren
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Interactive Tour */}
      <InteractiveTour 
        manualStart={showTourManual}
        onComplete={() => setShowTourManual(false)}
      />
      
      {/* PWA Install Banner */}
      
    </div>
  );
}